import './App.css';
import React, {useEffect, useState, Suspense, useMemo, useCallback} from 'react'
import Navigation from './components/Navigation'
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp'
import Backdrop from '@mui/material/Backdrop';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from './firebase-config'
import { AppData } from './Data/AppData'
import Container from '@mui/material/Container';
import SimpleLoader from './components/SimpleLoader';
import PageTransition from './components/PageTransition';

// Lazy load components
const TrainingPage = React.lazy(() => import('./pages/TrainingPage'));
const ProgressPage = React.lazy(() => import('./pages/ProgressPage'));
const MealPlan = React.lazy(() => import('./pages/MealPlan'));

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [page, setPage] = useState(() => {
    // Get page from localStorage to maintain navigation state on refresh
    const savedPage = localStorage.getItem('currentPage');
    return savedPage ? parseInt(savedPage, 10) : 0;
  });

  // Use useCallback to prevent recreation of this function on each render
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    localStorage.setItem('currentPage', newPage.toString());
  }, []);

  // Callback for setting user profile from SignUp component
  const handleSetUserProfile = useCallback((profile) => {
    console.log("Setting user profile:", profile);
    setUserProfile(profile);
    setLoading(false);
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setLoading(true);
      if (currentUser) {
        try {
          const docRef = doc(db, "users", currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setUserProfile(docSnap.data());
            setUser(currentUser);
            setLoading(false);
          } else {
            console.log("No user profile found. Showing signup form.");
            setUser(currentUser);
            setLoading(false);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setLoading(false);
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Memoize the current page component to prevent unnecessary re-renders
  const CurrentPage = useMemo(() => {
    // Return a component function that won't change unless page changes
    return function MemoizedCurrentPage() {
      let content;
      switch (page) {
        case 0:
          content = <TrainingPage />;
          break;
        case 1:
          content = <MealPlan />;
          break;
        case 2:
          content = <ProgressPage />;
          break;
        default:
          content = <TrainingPage />;
      }
      
      return (
        <PageTransition transitionKey={page}>
          {content}
        </PageTransition>
      );
    };
  }, [page]);

  if (loading) {
    return (
      <Backdrop
        sx={{ color: '#fff', bgcolor: "#192327", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={true}
      >
        <Container component="main" maxWidth="xs">
          <Stack spacing={2}>
            <img width={'100%'} src="./logo.png" alt="Taekwondo Image" />
            <LinearProgress color="success" />
          </Stack>
        </Container>
      </Backdrop>
    );
  }

  // If no user is authenticated, show sign in
  if (!user) {
    return (
      <div className="App">
        <SignIn />
      </div>
    );
  }

  // If user is authenticated but has no profile, show sign up
  if (user && !userProfile) {
    return (
      <div className="App">
        <Backdrop
          sx={{ color: '#fff', bgcolor: "#192327", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <Container component="main" maxWidth="xs" id="create-profile">
            <Stack height={'100vh'} overflow={'scroll'} justifyContent={'center'} spacing={2}>
              <SignUp email={user.email} uid={user.uid} setProfile={handleSetUserProfile} />
            </Stack>
          </Container>
        </Backdrop>
      </div>
    );
  }

  // If user is authenticated and has a profile, show the main app
  return (
    <div className="App">
      <AppData userProfile={userProfile}>
        <Navigation page={page} setPage={handlePageChange}>
          <Suspense fallback={<SimpleLoader />}>
            <CurrentPage />
          </Suspense>
        </Navigation>
      </AppData>
    </div>
  );
}

export default App;
